/**
 * Manual form intake body — shared by /projects/new (page) and the calendar
 * modal. Renders the shared <ProjectDraftReview> field form plus the manual-only
 * document uploader; owns the create call. Page variant adds the route-based
 * IntakeHeader + server draft autosave; modal variant is seeded from a calendar
 * slot and stays lean (no autosave, no header).
 */
import { useEffect, useRef, useState } from 'react';
import { FileText, Image as ImageIcon, Paperclip, X } from 'lucide-react';
import { toast } from 'sonner';
import type { IntakeProposedTask, ProjectCreateInput } from '@vari-crew/schemas';
import { useCreateProject } from '@/api/projects';
import { deleteDraft } from '@/api/project-drafts';
import { useDraftAutosave } from '@/lib/useDraftAutosave';
import { IntakeHeader } from '@/components/intake/IntakeHeader';
import { ProjectDraftReview } from '@/components/intake/ProjectDraftReview';
import type { TimingModel } from '@/components/intake/timing/timingModel';
import { uploadProjectDocument } from '@/api/attachments';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { clearIntakeSession } from '@/lib/intakeSession';
import { draftToCreateInput } from '@/lib/intakeDraft';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Input';
import { isoToDay } from '@/lib/calendarGrid';
import {
  applySeed,
  useIntakeState,
  type IntakePanelProps,
} from '@/components/intake/intakePanelShared';

const DEFAULT_DRAFT: Partial<ProjectCreateInput> = {
  source: 'form',
  kind: 'install',
  priority: 'normal',
};

export function FormIntakePanel({ variant, seed, onCreated, onDirtyChange }: IntakePanelProps) {
  const create = useCreateProject();
  const { data: me } = useCurrentUser();
  const isPm = me?.role === 'pm';
  // Supervisors/admins plan the crew's tasks inline; PMs are requesters and
  // never see a task editor (the server still derives a breakdown from the kind).
  const canEditTasks = me?.role === 'supervisor' || me?.role === 'admin';
  const persist = variant === 'page';
  const kp = persist ? 'form' : 'cal.form';

  // Controlled draft + uploads + server-draft id. Page persists these across
  // screen switches (and resume rehydrates the same form.* slots); modal keeps
  // them ephemeral and seeds the draft from the calendar slot.
  const [draft, setDraft] = useIntakeState<Partial<ProjectCreateInput>>(persist, `${kp}.draft`, {
    ...DEFAULT_DRAFT,
    ...applySeed(null, seed),
  });
  const [files, setFiles] = useIntakeState<File[]>(persist, `${kp}.files`, []);
  const [proposedTasks, setProposedTasks] = useIntakeState<IntakeProposedTask[]>(
    persist,
    `${kp}.proposedTasks`,
    [],
  );
  // Supervisor "Plan the work" authoring model — persisted so the full per-block
  // roles/times/descriptions survive a screen switch or a resumed draft (the
  // flattened proposedTasks above can't round-trip them). Null for PMs.
  const [timingModel, setTimingModel] = useIntakeState<TimingModel | null>(
    persist,
    `${kp}.timingModel`,
    null,
  );
  const [draftId, setDraftId] = useIntakeState<string | null>(persist, `${kp}.draftId`, null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function patchDraft<K extends keyof ProjectCreateInput>(
    key: K,
    value: Partial<ProjectCreateInput>[K],
  ) {
    setDraft((d) => ({ ...(d ?? {}), [key]: value }));
  }

  // Report unsaved content so the modal can guard an accidental close.
  const dirty = !!(
    draft.title?.trim() ||
    draft.client_name?.trim() ||
    draft.site_address?.line1?.trim() ||
    files.length
  );
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Flexible task creation (Leo, demo #7 → #9): no auto-seed at all. The studio
  // starts from whatever the supervisor has — an empty plan is genuinely empty
  // (zero tasks), not a phantom blank "install" skeleton (which used to ride
  // through to creation as a spurious extra task). They add tasks explicitly or
  // opt in to the kind defaults via the studio's "Suggest tasks" button.

  function addFiles(incoming: File[]) {
    setFiles((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        if (next.length >= 8) {
          toast.warning('Up to 8 documents.');
          break;
        }
        if (!(f.type === 'application/pdf' || f.type.startsWith('image/'))) {
          toast.warning(`${f.name}: only PDFs and images can be attached.`);
          continue;
        }
        if (f.size > 25 * 1024 * 1024) {
          toast.warning(`${f.name} is too large (max 25 MB).`);
          continue;
        }
        if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
        next.push(f);
      }
      return next;
    });
  }

  // Autosave a resumable draft to the server (page only; survives a tab close).
  const saveState = useDraftAutosave({
    source: 'form',
    draftId,
    setDraftId,
    signature: `${JSON.stringify(draft)}|${proposedTasks.length}|${JSON.stringify(timingModel)}`,
    paused: !persist || submitting,
    build: () => ({
      payload: { draft, proposedTasks, timingModel },
      hints: {
        project_title: draft.title ?? null,
        kind: draft.kind ?? null,
        client: draft.client_name ?? null,
        deadline: isoToDay(draft.deadline_date) ?? null,
        built: false,
      },
      hasContent: !!(draft.title || draft.client_name || draft.site_address?.line1),
    }),
  });

  async function handleCreate() {
    // The slot is already seeded into `draft` at init, so submit the draft as-is
    // — re-applying the seed here would clobber any timing the user adjusted.
    const prepared = draftToCreateInput(draft, 'form');
    if (!prepared.ok) {
      toast.error(prepared.error);
      return;
    }
    try {
      setSubmitting(true); // pause autosave through submit → delete → navigate
      const created = await create.mutateAsync({
        ...prepared.value,
        // Supervisor's task plan — sent EXPLICITLY, even when empty, so an
        // intentionally task-less plan creates zero tasks (flexible task
        // creation, demo #7) instead of the server re-deriving the kind default.
        // PMs send none (omit) → server derives the default from the kind.
        ...(canEditTasks ? { proposed_tasks: proposedTasks } : {}),
      });
      // Attach any supporting documents (best-effort) so they ride along.
      let docFailures = 0;
      for (const f of files) {
        try {
          await uploadProjectDocument({ projectId: created.id, file: f });
        } catch {
          docFailures += 1;
        }
      }
      const noun = isPm ? 'Request submitted' : 'Project created';
      const docOk = files.length - docFailures;
      if (docFailures > 0) {
        toast.warning(
          `${noun}${docOk > 0 ? ` with ${docOk} document${docOk > 1 ? 's' : ''}` : ''}, but ${docFailures} document(s) couldn't be attached — add them from the project.`,
        );
      } else if (docOk > 0) {
        toast.success(`${noun} with ${docOk} document${docOk > 1 ? 's' : ''} · ${created.title}`);
      } else {
        toast.success(`${noun} · ${created.title}`);
      }
      if (persist) {
        if (draftId) void deleteDraft(draftId).catch(() => {});
        clearIntakeSession('form');
      }
      onDirtyChange?.(false);
      onCreated(created);
    } catch (err) {
      setSubmitting(false);
      toast.error(`Failed: ${(err as Error).message}`);
    }
  }

  return (
    <>
      {variant === 'page' && <IntakeHeader mode="form" isPm={isPm} save={saveState} />}

      <ProjectDraftReview
        draft={draft}
        onPatch={patchDraft}
        onSubmit={handleCreate}
        submitting={submitting || create.isPending}
        heading={isPm ? 'New request' : 'New project'}
        submitLabel={isPm ? 'Submit request' : 'Create project'}
        // Full-page intake is the guided-steps wizard; the calendar modal keeps
        // the single-scroll review (a quick, seeded path).
        layout={variant === 'page' ? 'wizard' : 'scroll'}
        proposedTasks={canEditTasks ? proposedTasks : undefined}
        onProposedTasksChange={canEditTasks ? setProposedTasks : undefined}
        initialTimingModel={canEditTasks ? timingModel : undefined}
        onTimingModelChange={canEditTasks ? setTimingModel : undefined}
        allowSignoff={canEditTasks}
        footer={
          <div>
            <Label>Documents (optional)</Label>
            <p className="text-xs text-ink-faint mb-2">
              Attach the sales order, packing slip, or design package if you have them — PDFs or
              images. They stay attached to the
              {isPm ? ' request' : ' project'}.
            </p>
            {files.length > 0 && (
              <ul className="space-y-1 mb-2">
                {files.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 px-3 py-1.5 rounded border border-line"
                  >
                    {f.type.startsWith('image/') ? (
                      <ImageIcon className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                    ) : (
                      <FileText className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                    )}
                    <span className="text-sm truncate flex-1">{f.name}</span>
                    <span className="text-[11px] text-ink-faint shrink-0">
                      {f.size < 1024 * 1024
                        ? `${(f.size / 1024).toFixed(0)} KB`
                        : `${(f.size / 1024 / 1024).toFixed(1)} MB`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      aria-label={`Remove ${f.name}`}
                      className="text-ink-faint hover:text-ink"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                addFiles(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="w-3.5 h-3.5" /> Attach documents
            </Button>
          </div>
        }
      />
    </>
  );
}
